import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { parsePhoneNumber, AsYouType, CountryCode } from 'libphonenumber-js';
import Flag from 'react-world-flags';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getCountryName } from '@/lib/countryUtils';

interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  defaultCountry?: CountryCode;
  showCountrySelect?: boolean;
}

// Common country codes for the selector
const COUNTRY_OPTIONS = [
  { code: 'US', name: 'United States', callingCode: '1' },
  { code: 'GB', name: 'United Kingdom', callingCode: '44' },
  { code: 'CA', name: 'Canada', callingCode: '1' },
  { code: 'AU', name: 'Australia', callingCode: '61' },
  { code: 'DE', name: 'Germany', callingCode: '49' },
  { code: 'FR', name: 'France', callingCode: '33' },
  { code: 'ES', name: 'Spain', callingCode: '34' },
  { code: 'IT', name: 'Italy', callingCode: '39' },
  { code: 'IN', name: 'India', callingCode: '91' },
  { code: 'BR', name: 'Brazil', callingCode: '55' },
  { code: 'MX', name: 'Mexico', callingCode: '52' },
  { code: 'JP', name: 'Japan', callingCode: '81' },
  { code: 'CN', name: 'China', callingCode: '86' },
  { code: 'KR', name: 'South Korea', callingCode: '82' },
  { code: 'NL', name: 'Netherlands', callingCode: '31' },
  { code: 'CH', name: 'Switzerland', callingCode: '41' },
  { code: 'SE', name: 'Sweden', callingCode: '46' },
  { code: 'NO', name: 'Norway', callingCode: '47' },
  { code: 'DK', name: 'Denmark', callingCode: '45' },
  { code: 'FI', name: 'Finland', callingCode: '358' },
];

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  defaultCountry = 'US',
  showCountrySelect = true,
  className = '',
  placeholder = 'Phone number',
  ...props
}) => {
  const [country, setCountry] = useState<CountryCode>(defaultCountry);
  const [displayValue, setDisplayValue] = useState<string>('');

  // Parse the existing value to determine the country
  useEffect(() => {
    if (value && value.startsWith('+')) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed && parsed.country) {
          setCountry(parsed.country);
        }
      } catch (error) {
        // Keep default country if parsing fails
      }
    }
  }, [value]);

  // Format the display value based on the current value
  useEffect(() => {
    if (value) {
      try {
        const formatter = new AsYouType(country);
        let formatted = formatter.input(value);
        
        // Remove leading zeros from formatted value when country code is shown
        if (showCountrySelect && formatted.startsWith('0')) {
          formatted = formatted.substring(1);
        }
        
        setDisplayValue(formatted);
      } catch (error) {
        setDisplayValue(value);
      }
    } else {
      setDisplayValue('');
    }
  }, [value, country, showCountrySelect]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    
    // Remove leading zeros if user enters them when country code is shown
    if (showCountrySelect && inputValue.startsWith('0')) {
      inputValue = inputValue.substring(1);
    }
    
    const formatter = new AsYouType(country);
    const formatted = formatter.input(inputValue);
    
    setDisplayValue(formatted);
    
    // Try to get the E164 format for storage
    try {
      const parsed = parsePhoneNumber(inputValue, country);
      if (parsed && parsed.isValid()) {
        onChange(parsed.format('E.164'));
      } else {
        // If not valid yet, store the raw input
        onChange(inputValue);
      }
    } catch (error) {
      // If parsing fails, store the raw input
      onChange(inputValue);
    }
  };

  const handleCountryChange = (newCountry: string) => {
    const countryCode = newCountry as CountryCode;
    setCountry(countryCode);
    
    // Reformat the existing number with the new country
    if (displayValue) {
      try {
        const formatter = new AsYouType(countryCode);
        let formatted = formatter.input(displayValue);
        
        // Remove leading zeros when country code is shown
        if (showCountrySelect && formatted.startsWith('0')) {
          formatted = formatted.substring(1);
        }
        
        setDisplayValue(formatted);
        
        const parsed = parsePhoneNumber(displayValue, countryCode);
        if (parsed && parsed.isValid()) {
          onChange(parsed.format('E.164'));
        }
      } catch (error) {
        // Keep the existing value if reformatting fails
      }
    }
  };

  return (
    <TooltipProvider>
      <div className={`flex gap-2 ${className}`}>
        {showCountrySelect && (
          <Select value={country} onValueChange={handleCountryChange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2">
                      <Flag 
                        code={country} 
                        style={{ width: '1.25rem', height: '0.875rem' }}
                        fallback={<span className="text-xs">{country}</span>}
                      />
                      <span>
                        +{COUNTRY_OPTIONS.find(c => c.code === country)?.callingCode || '1'}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {getCountryName(country)}
                  </TooltipContent>
                </Tooltip>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  <div className="flex items-center gap-2">
                    <Flag 
                      code={option.code} 
                      style={{ width: '1.25rem', height: '0.875rem' }}
                      fallback={<span className="text-xs">{option.code}</span>}
                    />
                    <span className="flex-1">{option.name}</span>
                    <span className="text-muted-foreground">+{option.callingCode}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Input
          {...props}
          type="tel"
          value={displayValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="flex-1"
        />
      </div>
    </TooltipProvider>
  );
};